/**
 * Modal Field Adapter for Facilities
 * Transforms Facility Field Registry into Modal field configurations
 */

import { FieldConfig } from '../../../components/modals/ModalFieldRenderer';
import {
  Facility,
  FacilityStatus,
} from '../../../services/api/domains/facilities/types';
import { FACILITIES_FIELDS_REGISTRY } from '../fields';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import { validationHelpers } from '../../../components/modals/utils/validation';
import {
  formatDateForInput,
  formatDateTime,
  parseDateFromInput,
} from '../../../utils/dateConversion';

/**
 * Convert a basic field definition to modal field config
 */
function createStandardField(
  field: BaseFieldDefinition
): FieldConfig<Facility> {
  // Determine field type
  let fieldType: 'text' | 'textarea' | 'number' | 'date' | 'readonly' = 'text';

  if (field.isReadOnly) {
    fieldType = 'readonly';
  } else if (field.renderConfig?.rows && field.renderConfig.rows > 1) {
    fieldType = 'textarea';
  } else if (field.dataType === 'number') {
    fieldType = 'number';
  } else if (field.dataType === 'date') {
    fieldType = 'date';
  }

  const config: FieldConfig<Facility> = {
    key: field.key as keyof Facility,
    label: field.label,
    type: fieldType,
    getValue: (f: Facility) => {
      const value = f[field.key as keyof Facility];
      if (field.dataType === 'date') {
        return formatDateForInput(value as string | Date | null);
      }
      return value || (field.dataType === 'number' ? null : '');
    },
    setValue: field.isReadOnly
      ? undefined
      : (f: Facility, val: unknown) => {
          let processedValue = val;
          if (field.dataType === 'date') {
            processedValue = parseDateFromInput(val as string) || null;
          }
          return {
            ...f,
            [field.key]: processedValue === '' ? null : processedValue,
          };
        },
    isAlwaysDisabled: field.isReadOnly,
    isRequired: field.isRequired,
    tooltipText: field.description,
    rows: field.renderConfig?.rows,
  };

  // Add formatter for readonly date fields
  if (field.dataType === 'date' && field.isReadOnly) {
    config.formatter = formatDateTime;
  }

  // Add number field config
  if (field.dataType === 'number') {
    // Add step and helpText for latitude/longitude precision
    if (field.key === 'latitude') {
      config.min = -90;
      config.max = 90;
      config.step = 0.000001;
      config.helpText = 'Decimal degrees (-90 to 90)';
      config.validator = validationHelpers.latitude;
    } else if (field.key === 'longitude') {
      config.min = -180;
      config.max = 180;
      config.step = 0.000001;
      config.helpText = 'Decimal degrees (-180 to 180)';
      config.validator = validationHelpers.longitude;
    }
  }

  // Add validation for required fields
  if (field.isRequired) {
    const validators = [];
    validators.push(validationHelpers.required);
    // Add minLength for facility_name
    if (field.key === 'facility_name') {
      validators.push(validationHelpers.minLength(2));
    }
    config.validator = validationHelpers.combine(...validators);
  }

  // Add futureDate validator for qta_expiration_date
  if (field.key === 'qta_expiration_date') {
    config.validator = validationHelpers.futureDate;
    config.helpText = 'Date when QTA status expires (must be in the future)';
  }

  return config;
}

/**
 * Create facility status select field with predefined options
 */
function createFacilityStatusField(
  field: BaseFieldDefinition
): FieldConfig<Facility> {
  return {
    key: 'facility_status',
    label: field.label,
    type: 'select',
    getValue: (f: Facility) => f.facility_status || '',
    setValue: (f: Facility, val: unknown) => ({
      ...f,
      facility_status: (val as FacilityStatus) || null,
    }),
    options: [
      { value: 'APPROVED', label: 'APPROVED' },
      { value: 'INACTIVE', label: 'INACTIVE' },
      { value: 'DISQUALIFY', label: 'DISQUALIFY' },
      { value: 'R&D ONLY', label: 'R&D ONLY' },
      { value: 'ON HOLD', label: 'ON HOLD' },
      { value: 'NOT MANUF.', label: 'NOT MANUF.' },
    ],
    isRequired: field.isRequired,
    tooltipText: field.description,
  };
}

/**
 * Convert Facility Field Registry to Modal field configurations
 * @returns Array of FieldConfig for Modal
 */
export function toModalFields(): FieldConfig<Facility>[] {
  const fields: FieldConfig<Facility>[] = [];

  for (const field of FACILITIES_FIELDS_REGISTRY) {
    // Skip computed fields (not editable)
    if (field.isComputed) {
      continue;
    }

    // Skip internal ID fields (not shown in modal)
    if (
      field.key === 'facility_id' ||
      field.key === 'manufacturer_id' ||
      field.key === 'manufacturer_facility_count'
    ) {
      continue;
    }

    // Handle facility_status select field
    if (field.key === 'facility_status') {
      fields.push(createFacilityStatusField(field));
      continue;
    }

    // Handle standard fields (string, number, date, readonly)
    if (field.dataType !== 'array' && field.dataType !== 'object') {
      fields.push(createStandardField(field));
    }
  }

  return fields;
}

/**
 * Get always disabled field names for facilities
 */
export function getAlwaysDisabledFields(): string[] {
  return FACILITIES_FIELDS_REGISTRY.filter(f => f.isReadOnly).map(
    f => f.key as string
  );
}

/**
 * Get facility ID field for display in view mode
 */
export function getFacilityIdField(): FieldConfig<Facility> {
  return {
    key: 'facility_id',
    label: 'Facility ID',
    type: 'readonly',
    getValue: f => f.facility_id?.toString() || '',
    isAlwaysDisabled: true,
  };
}
